import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PiArrowCounterClockwise } from 'react-icons/pi';

import { FMS_READ_SCOPE } from '#/api/fmsMgmt/fmsReadScope';
import { fetchVoucherList } from '#/api/fmsMgmt/voucher';
import { PERM } from '#/config/permissions';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { mergeFilterUpdate } from '#/modules/commonComponents/table/listFilterKeys';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import { createSelectColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import {
  buildVoucherPickerColumns,
  toVoucherSummary,
} from '#/modules/finMgmt/claims/data/voucherPickerColumns';
import { VOUCHER_LIST_SORT_FIELD_LABELS } from '#/modules/finMgmt/vouchers/helper/voucherStatus';
import { useCan } from '#/modules/infra/auth/useCan';
import { voucherListQueryKey } from '#/utils/queryKeys';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT = [{ id: 'voucherDate', desc: true }];
/** 請款單只能引用有效憑證（read-routes：選擇憑證時只傳 status=ACTIVE） */
const LOCKED_FILTERS = { status: 'ACTIVE' };

const EMPTY_PAGE = {
  current_page: 1,
  page_size: DEFAULT_PAGE_SIZE,
  total_count: 0,
  total_page: 1,
};

function buildInitialSelection(initialSelectedRows = []) {
  const rowSelection = {};
  const selectedById = {};
  initialSelectedRows.forEach((row) => {
    if (!row?.id) {
      return;
    }
    const id = String(row.id);
    rowSelection[id] = true;
    selectedById[id] = toVoucherSummary(row);
  });
  return { rowSelection, selectedById };
}

/**
 * 請款單選擇憑證：server-side 搜尋／篩選／排序／分頁，多選；跨頁保留勾選。
 * 父層每次開啟換 key 重掛，初始勾選取自 initialSelectedRows。
 */
const VoucherPickerDialog = ({
  open,
  onOpenChange,
  initialSelectedRows = [],
  onConfirm,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [keyword, setKeywordState] = useState('');
  const [sort, setSortState] = useState(DEFAULT_SORT);
  const [userFilters, setUserFilters] = useState({});
  const [searchKeyword, setSearchKeyword] = useState('');

  const listQuery = useMemo(() => ({
    page,
    pageSize,
    keyword,
    sort,
    filters: { ...userFilters, ...LOCKED_FILTERS },
  }), [page, pageSize, keyword, sort, userFilters]);

  const initialSelection = useMemo(
    () => buildInitialSelection(initialSelectedRows),
    [initialSelectedRows],
  );
  const [selectedById, setSelectedById] = useState(initialSelection.selectedById);

  // be doc fms-read-scope：可引用「有直接讀取權」的憑證；有 `.read` 走原路由看全部，否則 `/self`
  const canReadAllVouchers = useCan(PERM.FMS_VOUCHER_READ);
  const scope = canReadAllVouchers ? FMS_READ_SCOPE.ALL : FMS_READ_SCOPE.SELF;
  const listQueryResult = useQuery({
    queryKey: voucherListQueryKey(listQuery, scope),
    queryFn: () => fetchVoucherList(listQuery, { scope }),
    enabled: open,
    placeholderData: keepPreviousData,
  });
  const rows = useMemo(
    () => listQueryResult.data?.data ?? [],
    [listQueryResult.data?.data],
  );

  const setPageSize = useCallback((next) => {
    setPageSizeState(next);
    setPage(1);
  }, []);

  const setKeyword = useCallback((next) => {
    setKeywordState(next ?? '');
    setPage(1);
  }, []);

  const setFilter = useCallback((key, value) => {
    if (key in LOCKED_FILTERS) {
      return;
    }
    setUserFilters((prev) => mergeFilterUpdate(prev, key, value));
    setPage(1);
  }, []);

  const handleSortingChange = useCallback((sorting) => {
    setSortState((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
    setPage(1);
  }, []);

  const removeSortAt = useCallback((index) => {
    setSortState((prev) => (prev ?? []).filter((_, sortIndex) => sortIndex !== index));
    setPage(1);
  }, []);

  const resetQuery = useCallback(() => {
    setPage(1);
    setPageSizeState(DEFAULT_PAGE_SIZE);
    setKeywordState('');
    setSortState(DEFAULT_SORT);
    setUserFilters({});
    setSearchKeyword('');
  }, []);

  const columns = useMemo(() => ([
    createSelectColumn({ enableMultiSelection: true, showHeaderCheckbox: true }),
    ...buildVoucherPickerColumns({
      filters: listQuery.filters,
      onFilterChange: setFilter,
    }),
  ]), [listQuery.filters, setFilter]);

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleRowSelectionChange = useCallback((selection) => {
    setSelectedById((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        const id = String(row.id);
        if (selection[id]) {
          next[id] = toVoucherSummary(row);
        } else {
          delete next[id];
        }
      });
      return next;
    });
  }, [rows]);

  function handleConfirm() {
    onConfirm(Object.values(selectedById).filter(Boolean));
    onOpenChange(false);
  }

  const selectedCount = Object.keys(selectedById).length;

  const headerSearch = (
    <div className="flex w-full max-w-md items-center gap-2 sm:w-72">
      <TableSearchInput
        useKeyword
        keyword={searchKeyword}
        onKeywordChange={setSearchKeyword}
        onSearch={() => setKeyword(searchKeyword)}
        placeholder="搜尋憑證號碼或備註"
        className="min-w-0 w-full text-foreground"
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className={cn('hover:text-gray-50', 'size-10 shrink-0')}
            aria-label="清除搜尋與篩選"
            onClick={resetQuery}
          >
            <PiArrowCounterClockwise size={20} aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">清除搜尋與篩選</TooltipContent>
      </Tooltip>
    </div>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="選擇憑證"
      description={`只列出有效憑證，可跨頁勾選。已選 ${selectedCount} 張。`}
      headerVariant="primary"
      headerEnd={headerSearch}
      showCloseButton={false}
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      onCancel={() => onOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText="確認選取"
      cancelText="取消"
      cancelAriaLabel="取消選擇憑證"
      confirmDisabled={listQueryResult.isLoading}
    >
      <ListTablePanel
        variant="plain"
        columns={columns}
        rows={rows}
        pagination={listQueryResult.data?.page ?? EMPTY_PAGE}
        isLoading={listQueryResult.isLoading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        listQuery={listQuery}
        filterStatusFields={filterStatusFields}
        sortFieldLabels={VOUCHER_LIST_SORT_FIELD_LABELS}
        onFilterChange={setFilter}
        onKeywordClear={() => {
          setSearchKeyword('');
          setKeyword('');
        }}
        onSortRemove={removeSortAt}
        onSortingChange={handleSortingChange}
        enableMultiSelection
        paginationClassName="px-0 py-2"
        initialRowSelection={initialSelection.rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
      />
    </AppDialog>
  );
};

VoucherPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  initialSelectedRows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    voucherNo: PropTypes.string,
  })),
  onConfirm: PropTypes.func.isRequired,
};

export default VoucherPickerDialog;
